/**
 * Issue Actions API Service
 *
 * Service functions for interacting with the issue actions API endpoints.
 */

import { apiClient } from "./api";
import type { IssueAction, ActionCategory, ActionType } from "@/types/issueAction";

const API_PREFIX = "/api/v1/security-issues";

/**
 * Paginated response from backend
 */
export interface PaginatedActionsResponse {
  data: IssueAction[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Request body for executing an action
 */
export interface ExecuteActionData {
  action_type: ActionType;
  action_category: ActionCategory;
  parameters?: Record<string, unknown>;
}

/**
 * List actions for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export async function listIssueActions(
  issueId: number,
  page: number = 1
): Promise<PaginatedActionsResponse> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  return apiClient.get<PaginatedActionsResponse>(
    `${API_PREFIX}/${issueId}/actions?${params.toString()}`
  );
}

/**
 * Execute an action on a security issue.
 * @param issueId - The numeric ID of the issue
 * @param data - The action execution data
 * @param force - If true, skip server-side cache and re-execute the action
 */
export async function executeAction(
  issueId: number,
  data: ExecuteActionData,
  force = false
): Promise<IssueAction> {
  const params = force ? "?force=true" : "";
  return apiClient.post<IssueAction>(
    `${API_PREFIX}/${issueId}/actions${params}`,
    data
  );
}

/**
 * Get a specific action by ID.
 * @param issueId - The numeric ID of the issue
 * @param actionId - The numeric ID of the action
 */
export async function getActionById(
  issueId: number,
  actionId: number
): Promise<IssueAction> {
  return apiClient.get<IssueAction>(
    `${API_PREFIX}/${issueId}/actions/${actionId}`
  );
}

/**
 * Get the latest completed action of a given type for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param actionType - The action type to look up
 */
export async function getLatestAction(
  issueId: number,
  actionType: string
): Promise<IssueAction> {
  return apiClient.get<IssueAction>(
    `${API_PREFIX}/${issueId}/actions/latest/${actionType}`
  );
}
